/**
 * Formats a Date object into a human-readable string.
 * @param {Date} date - The Date object to format.
 * @param {Intl.DateTimeFormatOptions} [formatOptions] - Optional Intl.DateTimeFormatOptions to customize the format.
 * @returns {string} A formatted date string.
 */
const formatDate = (date: Date, formatOptions?: Intl.DateTimeFormatOptions): string => {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...formatOptions,
  });
};

/**
 * Formats an epoch timestamp to a UTC date string in dd/MM/yyyy format.
 * Truncates to 10 digits (seconds precision) to handle timestamps with extra trailing zeroes.
 * @param {number | null | undefined} epoch - The epoch timestamp to format
 * @returns {string} Formatted date string or '-' for null/undefined/invalid values
 */
const formatEpochToUTC = (epoch: number | null | undefined): string => {
  if (epoch == null) return '-';
  // Values ≥ 13 digits are milliseconds (API quirk, epoch is 13 digits with zero buffers); 10–11 digit values are seconds
  const epochSeconds = epoch >= 1_000_000_000_000 ? Math.floor(epoch / 1000) : epoch;
  const date = new Date(epochSeconds * 1000);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export { formatDate, formatEpochToUTC };
